import { useEffect, useState, useRef, useLayoutEffect } from 'react'

import { Loader } from '../../common/components/loader'
import { errorHandlerService } from '../../common/services/error-handler.service'
import { blockDataService } from './block-data.service'

import { useStyles } from './chart-loader.style'
import { LOADER_HEIGHT } from '../../common/components/loader.style'

const DEFAULT_INITIAL_HEIGHT = 400

export const ChartLoader = function ({ children, report, blockKey, dataKey, sliderDefinition, selections, splitChart, splitChartSelections, width, initialHeight, onDataLoad, fullHeight = false }) {
  const classes = useStyles()
  const [isLoading, setIsLoading] = useState(false)
  const [data, setData] = useState()
  const [loaderContainerHeight, setLoaderContainerHeight] = useState(initialHeight || DEFAULT_INITIAL_HEIGHT)
  const [adjustedLoaderStyle, setAdjustedLoaderStyle] = useState()

  const rootRef = useRef()
  const shouldShowLoader = useRef(true)
  const currentRefDataSource = useRef({})
  const refData = useRef()
  const selectionsRef = useRef()
  const splitChartSelectionsRef = useRef()

  useEffect(() => {
    if (!selections) {
      return
    }

    let refDataPromise

    // Currently, only one is supported but it could be more in the future.
    const refDataSource = selections.refDataSources?.[0]
    if (refDataSource) {
      if (isSameRefDataSource(refDataSource)) {
        // already has reference data
        refDataPromise = new Promise((resolve) => {
          resolve(refData.current)
        })
      } else {
        // fetch reference data
        refDataPromise = blockDataService.fetchReferenceData(refDataSource)
        refDataPromise.then((data) => {
          refData.current = data
          return data
        })
      }

      currentRefDataSource.current = refDataSource
    }

    getBlockData(refDataPromise)
    selectionsRef.current = selections
    splitChartSelectionsRef.current = splitChartSelections
  }, [selections, splitChartSelections])

  // automatically adjust where the loading indicator and the "no data" message should be displayed.
  // Might be a good case for useLayoutEffect to avoid async and measure properly
  // https://kentcdodds.com/blog/useeffect-vs-uselayouteffect - https://reactjs.org/blog/2020/08/10/react-v17-rc.html#effect-cleanup-timing
  useLayoutEffect(() => {
    const rootRefHolder = rootRef.current
    if (!data) {
      return
    }

    let previousEntry = { intersectionRatio: 0, boundingClientRect: { top: 9999999 } }
    const observer = new IntersectionObserver(([entry]) => {
      shouldShowLoader.current = entry.isIntersecting
      setLoaderContainerHeight(entry.boundingClientRect.height || DEFAULT_INITIAL_HEIGHT)

      const shouldAdjustLoaderPosition = (entry.intersectionRatio > 0.1) &&
        // Make sure that the loader does not change position when scrolling back up
        ((entry.intersectionRatio > previousEntry.intersectionRatio && entry.boundingClientRect.top < previousEntry.boundingClientRect.top) ||
        (entry.intersectionRatio < previousEntry.intersectionRatio && entry.boundingClientRect.top > previousEntry.boundingClientRect.top))

      if (shouldAdjustLoaderPosition) {
        const adjustedLoaderTop = (entry.intersectionRect.height - LOADER_HEIGHT) / 2
        setAdjustedLoaderStyle(shouldAdjustLoaderPosition ? { margin: `${adjustedLoaderTop}px auto auto` } : {})
      }

      previousEntry = entry
    }, { threshold: [0, 0.1, 0.15, 0.2, 0.25, 0.3, 0.35, 0.4, 0.45, 0.5, 0.55, 0.6, 0.65, 0.7, 0.75, 0.8, 0.85, 0.9, 0.95, 1] })

    if (rootRefHolder) {
      observer.observe(rootRefHolder)
    }

    return () => {
      observer.unobserve(rootRefHolder)
    }
  }, [isLoading])

  async function getBlockData (refDataPromise) {
    let blockData
    let splitChartBlockData

    if (shouldShowLoader.current) {
      setIsLoading(true)
    }

    // Reuse the same data if selections have not changed since the last fetch
    const previous = selectionsRef.current
    const previousSplitChartSelections = splitChartSelectionsRef.current
    const requiresFetch = !(
      previous &&
      previous.display?.value === selections.display?.value &&
      previous.strata?.value === selections.strata?.value &&
      isDeepEqual(previous.filters, selections.filters) &&
      isDeepEqual(previousSplitChartSelections, splitChartSelections)
    )

    let dataFetchPromises = []
    if (!requiresFetch) {
      setIsLoading(false)
      return
    } else if (requiresFetch && !splitChart) {
      dataFetchPromises = [
        loadBlockData(selections),
        Promise.resolve()
      ]
    } else {
      const filters = splitChartSelections?.values ? splitChartSelections.values.map((splitChartSelectValue) => ({
        ...selections.filters,
        [splitChartSelections.key]: splitChartSelectValue
      })) : [selections.filters, selections.filters]

      dataFetchPromises.push(loadBlockData({ ...selections, filters: filters[0] }))
      dataFetchPromises.push(loadBlockData({ ...selections, filters: filters[1] }))
    }

    try {
      [blockData, splitChartBlockData] = await Promise.all([...dataFetchPromises, refDataPromise])
    } catch (error) {
      errorHandlerService.handleServerError(error, 'Oops! An error occurred while retrieving data. Try again?')
      return
    } finally {
      setIsLoading(false)
    }

    setData(blockData)

    if (requiresFetch && splitChart) {
      onDataLoad([blockData, splitChartBlockData], refData.current)
    } else if (requiresFetch) {
      onDataLoad(blockData, refData.current)
    }
  }

  function loadBlockData (selections) {
    const sliderKey = sliderDefinition && sliderDefinition.key

    // Convert filter selection to key/value
    const filters = Object.entries(selections.filters).reduce((filters, [key, { value }]) => {
      filters[key] = value
      return filters
    }, {})

    return blockDataService.fetchBlockData(report, blockKey, selections.display?.value, selections.strata?.value, filters, sliderKey, dataKey)
  }

  function isSameRefDataSource (refDataSource) {
    return (refDataSource.region === currentRefDataSource.current.region) &&
      (refDataSource.regionLevel === currentRefDataSource.current.regionLevel) &&
      (refDataSource.subgroup === currentRefDataSource.current.subgroup)
  }

  return <div ref={rootRef} data-qa="chart-loader-root">
    {data
      ? (
        data.length === 0 && !splitChart
          ? (
            <div className={classes.container} style={{ width, height: loaderContainerHeight }} data-qa="chart-loader">
              {isLoading
                ? (
                  <Loader style={adjustedLoaderStyle} />
                )
                : (
                  <div style={adjustedLoaderStyle}>
                    The filter combination you selected does not have any data associated with it.<br />
                    Please select a different combination.
                  </div>
                )
              }
            </div>
          )
          : (
            <div style={{ height: (fullHeight ? '100%' : '100%') }}className={classes.chartContainer} data-qa="chart-container">
              {children}
              {isLoading && (
                <div className={classes.chartLoaderWithData}>
                  <Loader style={adjustedLoaderStyle} />
                </div>
              )}
            </div>
          )
      )
      : (
        <div className={classes.container} style={{ width, height: loaderContainerHeight }}>
          <Loader style={adjustedLoaderStyle} />
        </div>
      )}
  </div>
}

function isDeepEqual (val1, val2) {
  return JSON.stringify(val1 || {}) === JSON.stringify(val2 || {})
}
